import { useEffect, useState } from 'react';
import {
  SimpleForm,
  TextInput,
  BooleanInput,
  AutocompleteInput,
  required,
  regex,
  useRecordContext
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { formatCustomerId } from '../../../../helpers/googleAdsBridgesHelpers';
import { fetchAccounts } from '../../../../utils/accountUtils';
import { getSelectedIndustryId } from '../../../../utils/industryUtils';
import { PLATFORMS } from '../../../../config/constants/reportConstants';
import {
  BRIDGE_CUSTOMER_ID_REGEX,
  BRIDGE_CUSTOMER_ID_LABEL,
  BRIDGE_CUSTOMER_ID_MESSAGE
} from '../../../../config/constants/googleAdsBridgeConstants';
import { RotateSecretButton } from './components/RotateSecretButton';
import { ShowSecretButton } from './components/ShowSecretButton';

// Lives inside the form so it can reach the form context
const CustomerIdSync = ({ accounts, isEdit }) => {
  const accountId = useWatch({ name: 'internal_account_id' });
  const { setValue } = useFormContext();
  const record = useRecordContext();

  // The Account ID follows the selected account; an edited bridge keeps its stored value until the account changes
  useEffect(() => {
    if (!accountId) return;
    if (isEdit && record && accountId === record.internal_account_id) {
      setValue('customer_id', record.customer_id, { shouldDirty: true, shouldValidate: true });
      return;
    }
    const account = accounts.find((item) => item.id === accountId);
    if (account) {
      setValue('customer_id', formatCustomerId(account.account_id), { shouldDirty: true, shouldValidate: true });
    }
  }, [accountId, accounts, isEdit, record, setValue]);

  return null;
};

export const GoogleAdsBridgesForm = ({ isEdit = false, ...props }) => {
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    const loadAccounts = async () => {
      const { account_names, account_ids } = await fetchAccounts({
        platforms_filter: [PLATFORMS.GOOGLE],
        industry_id: getSelectedIndustryId()
      });
      setAccounts((account_names || []).map((account) => ({
        ...account,
        account_id: ((account_ids || []).find((item) => item.id === account.id) || {}).name
      })));
    };

    loadAccounts();
  }, []);

  return (
    <SimpleForm {...props}>
      <CustomerIdSync accounts={accounts} isEdit={isEdit} />
      {isEdit && <TextInput source="id" label="ID" disabled />}
      <AutocompleteInput
        source="internal_account_id"
        label="Account"
        choices={accounts}
        optionText="name"
        optionValue="id"
        validate={required()}
        className="input-width-lg"
      />
      <TextInput
        source="customer_id"
        label={BRIDGE_CUSTOMER_ID_LABEL}
        InputProps={{ readOnly: true }}
        helperText="Filled from the selected account"
        validate={[required(), regex(BRIDGE_CUSTOMER_ID_REGEX, BRIDGE_CUSTOMER_ID_MESSAGE)]}
      />
      <BooleanInput source="enabled" label="Enabled" />
      {isEdit && <ShowSecretButton />}
      {isEdit && <RotateSecretButton />}
    </SimpleForm>
  );
};
